/**
 * QorTrace Labs · Guided Tour — "Skim the methodology".
 *
 * Converts a curious-reader-with-no-map into a discovery-call-booker
 * by walking them through the 7 most conversion-bearing slabs of the
 * /labs hub on rails — they don't have to figure out the navigation
 * themselves.  Triggered by a single "SKIM THE METHODOLOGY" link
 * inline with the hero meta strip.
 *
 * Implementation notes
 * ────────────────────
 *   - One floating overlay anchored bottom-center; non-modal so the
 *     page is still scrollable / interactive underneath. The user can
 *     also dismiss with Esc.
 *   - Each step targets a single existing DOM element via testid.
 *     We DO NOT mutate the target's markup — only add a
 *     `.is-tour-target` class for the duration of the step.  The
 *     class drives a soft cyan halo via CSS, so on tour-exit the
 *     page is visually identical to its pre-tour state.
 *   - Smooth-scroll uses `scrollIntoView({block:'center'})` so the
 *     highlighted section lands roughly mid-viewport.
 *   - PostHog funnel events fire at start / step / completion /
 *     dismissal so we can compute step-survival rate.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowLeft, ArrowRight, X, MapPin,
} from "lucide-react";
import { trackEvent } from "../../lib/analytics";


// Each tour step is a SINGLE existing testid on /labs. Ordered so the
// narrative arc matches what a buying-side CISO would read top-to-
// bottom — thesis → what we ship → how we ship it → who ships it →
// what you take home → schedule the call.
const TOUR_STEPS = [
  { id: "labs-manifesto",
    title: "The thesis",
    body:  "Why we exist — your customers' encrypted data today is the decryption target of 2034. Read the slab and the four-pillar pitch beside it." },
  { id: "labs-services",
    title: "What we ship",
    body:  "Four senior-led engagements: PQC inventory, migration engineering, smart-contract audit, and a CISO readiness workshop." },
  { id: "labs-process",
    title: "How we work",
    body:  "Predictable four-phase methodology. Discover → Map → Migrate → Sign. Auditable at every gate." },
  { id: "labs-engagements",
    title: "What you take home",
    body:  "Every engagement ends with the same signed artefacts: PDF risk report, migration plan, and a registry-verifiable certificate." },
  { id: "labs-team-profiles",
    title: "Who runs the engagement",
    body:  "Senior engineers from day one — every member of this bench has shipped PQ in production. Pick the lead you want." },
  { id: "labs-hub-faq",
    title: "Frequently asked",
    body:  "Speed, risk, regulator expectations, what your procurement team needs to see — answered." },
  { id: "labs-inquiry-section",
    title: "Schedule a discovery call",
    body:  "If the above lands, the next step is a 30-minute call to scope your stack and price the engagement." },
];


// Imperative DOM helpers — kept here (not in React state) because the
// only side-effect is class-toggle on an element we don't own.
function clearAllHighlights() {
  document.querySelectorAll(".is-tour-target")
    .forEach((el) => el.classList.remove("is-tour-target"));
}
function highlightStep(step) {
  clearAllHighlights();
  const el = document.querySelector(`[data-testid='${step.id}']`);
  if (!el) return null;
  el.classList.add("is-tour-target");
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  return el;
}


export default function LabsTour({ open, onClose }) {
  const [stepIdx, setStepIdx] = useState(0);
  const startedAt = useRef(null);

  const step = TOUR_STEPS[stepIdx] || TOUR_STEPS[0];
  const lastIdx = TOUR_STEPS.length - 1;

  // Step transitions — fire `tour_step_viewed` for funnel survival
  // analysis and DOM-highlight the target.
  useEffect(() => {
    if (!open) return undefined;
    highlightStep(step);
    trackEvent("tour_step_viewed", { step: stepIdx + 1, slug: step.id });
    return undefined;
  }, [open, stepIdx, step]);

  // Open / close lifecycle — fire matching start + completion /
  // dismiss events with `step_reached` so the funnel knows where
  // people drop off.
  useEffect(() => {
    if (open) {
      startedAt.current = Date.now();
      setStepIdx(0);
      trackEvent("tour_started", { ts: startedAt.current });
    } else {
      clearAllHighlights();
    }
  }, [open]);

  const close = useCallback((reason) => {
    const dwell = startedAt.current ? Date.now() - startedAt.current : null;
    trackEvent("tour_dismissed", {
      reason,
      step_reached: stepIdx + 1,
      dwell_ms: dwell,
    });
    clearAllHighlights();
    onClose?.();
  }, [stepIdx, onClose]);

  const next = useCallback(() => {
    if (stepIdx < lastIdx) {
      setStepIdx((i) => i + 1);
    } else {
      trackEvent("tour_completed", {
        dwell_ms: startedAt.current ? Date.now() - startedAt.current : null,
      });
      clearAllHighlights();
      onClose?.();
    }
  }, [stepIdx, lastIdx, onClose]);

  // iter150: "Continue from here" — only available on the LAST step.
  // Fires a CustomEvent that the InquiryForm listens for: it sets
  // the message field to the tour-completion topic and auto-focuses
  // the work-email input.  Then we close the tour. Buyers who reach
  // step 7 are by far the highest-intent segment on the page; this
  // saves them the "I just learned, now I have to fill a form"
  // context switch.
  const continueFromHere = useCallback(() => {
    trackEvent("tour_continue_from_here", {
      step_reached: stepIdx + 1,
      dwell_ms: startedAt.current ? Date.now() - startedAt.current : null,
    });
    window.dispatchEvent(new CustomEvent("labs-prefill-inquiry", {
      detail: {
        source:  "tour-completed",
        message: "Completed the methodology walkthrough — ready to scope an engagement.",
      },
    }));
    clearAllHighlights();
    onClose?.();
  }, [stepIdx, onClose]);

  const prev = useCallback(() => {
    if (stepIdx > 0) setStepIdx((i) => i - 1);
  }, [stepIdx]);

  // Esc-to-dismiss when the tour is open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close("escape");
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev, close]);

  if (!open) return null;

  return (
    <div className="labs-tour-overlay" role="dialog"
         aria-label={`QorTrace Labs guided tour — step ${stepIdx + 1} of ${TOUR_STEPS.length}`}
         data-testid="labs-tour-overlay">
      <div className="labs-tour-card">
        <div className="labs-tour-progress" aria-hidden="true">
          {TOUR_STEPS.map((_, i) => (
            <span key={i}
                  className={`labs-tour-tick${i === stepIdx ? " is-active" : ""}${i < stepIdx ? " is-done" : ""}`} />
          ))}
        </div>
        <div className="labs-tour-meta">
          <span className="labs-tour-step-no">
            <MapPin size={11} /> STEP {String(stepIdx + 1).padStart(2, "0")} / {String(TOUR_STEPS.length).padStart(2, "0")}
          </span>
          <button type="button"
                  className="labs-tour-dismiss"
                  data-testid="labs-tour-skip"
                  onClick={() => close("skip")}
                  aria-label="Skip tour">
            <X size={13} /> Skip
          </button>
        </div>
        <div className="labs-tour-body">
          <h4 className="labs-tour-title" data-testid="labs-tour-title">{step.title}</h4>
          <p className="labs-tour-text">{step.body}</p>
        </div>
        <div className="labs-tour-actions">
          <button type="button"
                  className="labs-tour-btn secondary"
                  data-testid="labs-tour-prev"
                  onClick={prev}
                  disabled={stepIdx === 0}>
            <ArrowLeft size={12} /> Back
          </button>
          {stepIdx === lastIdx ? (
            <button type="button"
                    className="labs-tour-btn primary"
                    data-testid="labs-tour-continue"
                    onClick={continueFromHere}>
              Continue from here
              <ArrowRight size={12} />
            </button>
          ) : (
            <button type="button"
                    className="labs-tour-btn primary"
                    data-testid="labs-tour-next"
                    onClick={next}>
              Next
              <ArrowRight size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
